import { useMemo, useState } from "react";
import { BackLink } from "../ui/BackLink";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Avatar } from "../ui/Avatar";
import { EventSheet } from "../ui/EventSheet";
import { useAuth } from "../auth";
import {
  OWNERS,
  cancelEvent,
  cancelEventSeries,
  deleteEvent,
  markAttendance,
  rescheduleEventSeries,
} from "../actions";
import {
  daysFromNow,
  eventWhenText,
  usePeopleByIds,
  useEvent,
  useEventRegistrations,
  type EventRegistration,
} from "../data";
import { IcAlert, IcCalendar, IcCheck, IcChevron, IcPeople, IcTent } from "../ui/icons";

/**
 * One event: before, during and after (spec §4.8).
 *
 * The three phases are not tabs, they are the same screen answering a different
 * question as the date passes. Before: who has signed up. During: tick who
 * walked in. After: who came, and who did not.
 *
 * Check-in is the part with a time budget. §4.6 asks for a typical group marking
 * in under thirty seconds, which rules out a dialog per person: the list is
 * tappable rows with a running count, and one call saves the lot.
 */
export function EventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isStaff = role !== "viewer";
  const isAdmin = role === "owner" || role === "admin";

  const { event, loading, error } = useEvent(eventId);
  const regs = useEventRegistrations(eventId);

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveDays, setMoveDays] = useState<number[]>([]);
  const [deleteScope, setDeleteScope] = useState<"one" | "series">("one");
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelScope, setCancelScope] = useState<"one" | "series">("one");
  const [checkIn, setCheckIn] = useState(false);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const personIds = useMemo(
    () => regs.data.map((r) => r.primaryPersonId).filter((id): id is string => Boolean(id)),
    [regs.data],
  );
  // Capped at 30 by Firestore's `in` filter. Above that the names come from the
  // registration itself where present, and the row still works.
  const people = usePeopleByIds(personIds.slice(0, 30));
  const nameById = useMemo(
    () => new Map(people.data.map((p) => [p.id, p.displayName ?? ""])),
    [people.data],
  );

  if (loading) {
    return (
      <div className="screen">
        <div className="body">
          <div className="skeleton" style={{ height: 90, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 200 }} />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="screen">
        <div className="body">
          <BackLink fallback="/events" label="חזרה" />
          {error ? (
            <div className="notice error">
              לא הצלחנו לטעון את האירוע. זו תקלה, לא סימן שהאירוע לא קיים.
              <br />
              <span className="faint faint-xs mono">
                {error}
              </span>
            </div>
          ) : (
            <div className="empty">
              <div className="empty-ico">
                <IcTent size={26} />
              </div>
              <h3>האירוע לא נמצא</h3>
            </div>
          )}
        </div>
      </div>
    );
  }

  const days = daysFromNow(event.startAt);
  const isPast = days !== null && days < 0;
  const cancelled = event.status === "cancelled";
  const registered = regs.data.length;
  const guests = regs.data.reduce(
    (n, r) => n + (r.guestCounts?.adults ?? 0) + (r.guestCounts?.children ?? 0),
    0,
  );
  const attendedCount = regs.data.filter((r) => r.attended === true).length;

  async function saveAttendance() {
    if (!eventId) return;
    setBusy(true);
    setErr(null);
    try {
      const ids = [...ticked];
      const absent = regs.data.filter((r) => !ticked.has(r.id) && r.attended === true).map((r) => r.id);

      let marked = 0;
      let timelined = 0;
      if (ids.length > 0) {
        const r = await markAttendance(eventId, ids, true);
        marked += r.marked;
        timelined += r.timelined;
      }
      // Unticking somebody who was marked present removes the claim. A timeline
      // entry saying somebody was somewhere they were not is worse than no entry.
      if (absent.length > 0) await markAttendance(eventId, absent, false);

      setNote(
        `סימנו ש־${marked} הגיעו${timelined > 0 ? `, ול־${timelined} זה נרשם בציר הזמן` : ""}.` +
          (absent.length > 0 ? ` ביטלנו ${absent.length} סימונים.` : ""),
      );
      setCheckIn(false);
      setTicked(new Set());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  /** Moves the whole series onto different days of the week. */
  async function reschedule() {
    if (!event?.seriesId || moveDays.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await rescheduleEventSeries(event.seriesId, moveDays);
      const kept = r.keptBooked + r.keptDetached;
      const parts = [`קבענו מחדש ${r.created} מפגשים.`];
      if (kept > 0) parts.push(`${kept} לא זזו, כי יש בהם הרשמות או שהזזתם אותם ידנית.`);
      if (r.keptPast > 0) parts.push('מפגשים שכבר היו נשארו כמו שהם.');
      setNote(parts.join(' '));
      setMoving(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'לא הצלחנו לשנות. נסו שוב.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!eventId) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await deleteEvent(eventId, deleteScope);
      // Nothing left to show, so leave rather than render a dead page.
      navigate("/events", {
        replace: true,
        state: {
          flash:
            r.eventsDeleted > 1
              ? `מחקנו ${r.eventsDeleted} מפגשים.${r.unlinkedRegistrations > 0 ? ` ${r.unlinkedRegistrations} הרשמות נותקו מהאירוע, אבל נשמרו.` : ""}`
              : `מחקנו את האירוע.${r.unlinkedRegistrations > 0 ? ` ${r.unlinkedRegistrations} הרשמות נותקו מהאירוע, אבל נשמרו.` : ""}`,
        },
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו למחוק. נסו שוב.");
      setBusy(false);
    }
  }

  async function doCancel() {
    if (!eventId) return;
    setBusy(true);
    setErr(null);
    try {
      if (cancelScope === "series" && event?.seriesId) {
        const r = await cancelEventSeries(event.seriesId, cancelReason.trim() || undefined);
        setNote(
          `ביטלנו ${r.cancelled} מפגשים מ־${r.fromDate} והלאה. במפגשים שכבר היו לא נגענו.`,
        );
      } else {
        await cancelEvent(eventId, cancelReason.trim() || undefined);
        setNote("ביטלנו את האירוע. הוא נשאר במערכת עם ההרשמות שלו.");
      }
      setCancelling(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לבטל. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <div className="body">
        <BackLink fallback="/events" label="חזרה" />

        <div className="person-hero">
          <div
            className="evt-thumb"
            style={{ flexShrink: 0, background: "var(--orange-soft)", width: 56, height: 56 }}
          >
            <IcCalendar size={24} />
          </div>
          <div className="grow">
            <h1>{event.title}</h1>
            <p className="faint" style={{ marginTop: 4 }}>
              {eventWhenText(event)}
              {event.hebrewLabel && ` · ${event.hebrewLabel}`}
            </p>
          </div>
        </div>

        {cancelled && (
          <div className="notice error">
            <b>האירוע בוטל.</b>
            {event.cancelReason ? ` ${event.cancelReason}` : ""} ההרשמות נשמרו, כדי שתוכלו
            להודיע לנרשמים.
          </div>
        )}

        {note && <div className="notice info">{note}</div>}
        {err && <div className="notice error">{err}</div>}

        {/* The operational line, before the detail: how many, and how many
            people that actually is. */}
        <div className="tiles-row">
          <div className="stat">
            <b>{registered}</b>
            <span>הרשמות</span>
          </div>
          <div className="stat">
            <b>{guests || "—"}</b>
            <span>נפשות</span>
          </div>
          {(isPast || attendedCount > 0) && (
            <div className="stat">
              <b>{attendedCount}</b>
              <span>הגיעו</span>
            </div>
          )}
          {event.capacity != null && (
            <div className="stat">
              <b>{event.capacity}</b>
              <span>מקומות</span>
            </div>
          )}
        </div>

        {/*
          The actions, where they can be found.

          These existed before as a faint two-pixel button inside the details
          card, three screens down on a phone, and the product owner's verdict
          was simply "אי אפשר לערוך אירוע" — which is what an action nobody can
          find amounts to. Editing and marking who came are the two things done
          most often here, so they are the first thing under the numbers.
        */}
        {isStaff && (
          <div className="btn-row" style={{ marginTop: 12, flexWrap: "wrap" }}>
            {!cancelled && (
              <button className="btn" onClick={() => setEditing(true)}>
                עריכת האירוע
              </button>
            )}
            {!cancelled && (
              <Link className="btn" to={`/rollcall?event=${event.id}`}>
                מי היה
              </Link>
            )}
            {!cancelled && event.seriesId && (
              <button
                className="btn"
                onClick={() => {
                  setMoving(true);
                  // Seeded from the rule as it stands, so opening the control
                  // shows the current day rather than an empty row.
                  const current = (event.recurrence?.byWeekday ?? []) as number[];
                  setMoveDays(
                    current.length > 0
                      ? current
                      : [event.startAt?.toDate?.().getDay() ?? 0],
                  );
                }}
              >
                שינוי יום בשבוע
              </button>
            )}
            {isAdmin && (
              <button
                className="btn"
                onClick={() => {
                  setDeleting(true);
                  setDeleteScope("one");
                }}
              >
                מחיקה
              </button>
            )}
          </div>
        )}

        {/*
          Moving a whole series.

          Until now the only way to change which day a class met was to edit
          each meeting by hand. The product owner did exactly that, got two
          edits in, and ended up with a Sunday parent and ten Monday
          occurrences — then saw the mixture in Google Calendar and assumed the
          sync was at fault. The missing control was here.
        */}
        {moving && (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="card-title">שינוי יום בשבוע</div>
            <div className="card-pad">
              <p className="faint" style={{ marginTop: 0 }}>
                המפגשים הבאים יעברו ליום שתבחרו. מפגשים שכבר היו לא זזים. גם מפגש שיש בו
                הרשמות, או שהזזתם ידנית, נשאר במקום.
              </p>
              <div className="chips" style={{ marginBottom: 10 }}>
                {["א", "ב", "ג", "ד", "ה", "ו", "ש"].map((letter, day) => (
                  <button
                    key={day}
                    className={moveDays.includes(day) ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() =>
                      setMoveDays((prev) =>
                        prev.includes(day)
                          ? prev.filter((d) => d !== day)
                          : [...prev, day].sort((a, b) => a - b),
                      )
                    }
                    disabled={busy}
                    aria-pressed={moveDays.includes(day)}
                    style={{ minWidth: 38, justifyContent: "center" }}
                  >
                    {letter}
                  </button>
                ))}
              </div>
              <div className="btn-row">
                <button
                  className="btn btn-primary"
                  onClick={reschedule}
                  disabled={busy || moveDays.length === 0}
                >
                  {busy ? "מעדכנים…" : "קבעו מחדש"}
                </button>
                <button className="btn" onClick={() => setMoving(false)} disabled={busy}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        {deleting && (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="card-title">מחיקת האירוע</div>
            <div className="card-pad">
              <p className="faint" style={{ marginTop: 0 }}>
                האירוע יימחק לגמרי. ההרשמות לא נמחקות: הן מתנתקות מהאירוע ונשארות.
                אם תיצרו אותו מחדש עם אותו מזהה, הן יתחברו אליו שוב. סימוני הנוכחות
                באירוע יימחקו איתו.
              </p>

              {event.seriesId && (
                <div className="chips" style={{ marginBottom: 10 }}>
                  <button
                    className={deleteScope === "one" ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setDeleteScope("one")}
                    disabled={busy || event.isSeriesParent === true}
                  >
                    רק המפגש הזה
                  </button>
                  <button
                    className={deleteScope === "series" ? "tagchip tagchip-on" : "tagchip"}
                    onClick={() => setDeleteScope("series")}
                    disabled={busy}
                  >
                    כל הסדרה
                  </button>
                </div>
              )}

              {event.isSeriesParent === true && (
                <p className="faint" style={{ marginTop: 0 }}>
                  זה המפגש הראשון בסדרה, והוא שקובע איך היא חוזרת. לכן אפשר
                  למחוק אותו רק יחד עם כל הסדרה. כדי לבטל תאריך אחד, השתמשו ב„ביטול” למטה.
                </p>
              )}

              <div className="btn-row">
                <button
                  className="btn btn-danger"
                  onClick={remove}
                  disabled={busy || (event.isSeriesParent === true && deleteScope !== "series")}
                >
                  {busy ? "מוחקים…" : deleteScope === "series" ? "מחקו את כל הסדרה" : "מחקו"}
                </button>
                <button className="btn" onClick={() => setDeleting(false)} disabled={busy}>
                  ביטול
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="detail-2col">
          <div>
            <div className="card">
              <div className="card-title" style={{ display: "flex", alignItems: "center" }}>
                <span className="grow">נרשמים</span>
                {isStaff && registered > 0 && !cancelled && (
                  <button
                    className="faint"
                    onClick={() => {
                      setCheckIn((v) => !v);
                      setTicked(
                        checkIn
                          ? new Set()
                          : new Set(regs.data.filter((r) => r.attended === true).map((r) => r.id)),
                      );
                    }}
                    style={{ padding: "2px 6px" }}
                  >
                    {checkIn ? "ביטול" : "מי מהנרשמים הגיע"}
                  </button>
                )}
              </div>

              {regs.error ? (
                <div style={{ padding: "6px 14px 18px" }}>
                  <div className="notice error" style={{ margin: 0 }}>
                    לא הצלחנו לטעון את ההרשמות. זו תקלה, לא סימן שאין נרשמים.
                  </div>
                </div>
              ) : registered === 0 ? (
                <div className="empty" style={{ padding: "22px 22px" }}>
                  <p>
                    אין עדיין הרשמות. הרשמות מהאתר יתחברו לכאן לבד אם המזהה בטופס תואם
                    {event.slug && (
                      <>
                        {" "}
                        (<span className="mono" dir="ltr">{event.slug}</span>)
                      </>
                    )}
                    .
                  </p>
                </div>
              ) : (
                <>
                  {checkIn && (
                    <div style={{ padding: "0 14px 8px" }}>
                      <p className="faint">
                        סמנו מי הגיע. אפשר ללחוץ על כל השורה ולשמור פעם אחת לכולם. כל מי
                        שסימנתם יקבל רישום בציר הזמן שלו, וזה נחשב קשר.
                      </p>
                    </div>
                  )}

                  {regs.data.map((r, i) => (
                    <RegRow
                      key={r.id}
                      reg={r}
                      name={r.primaryPersonId ? nameById.get(r.primaryPersonId) ?? "" : ""}
                      last={i === regs.data.length - 1}
                      checkIn={checkIn}
                      ticked={ticked.has(r.id)}
                      onToggle={() =>
                        setTicked((prev) => {
                          const next = new Set(prev);
                          if (next.has(r.id)) next.delete(r.id);
                          else next.add(r.id);
                          return next;
                        })
                      }
                    />
                  ))}

                  {checkIn && (
                    <div style={{ padding: "10px 14px 14px" }}>
                      <div className="btn-row">
                        <button className="btn btn-primary" onClick={saveAttendance} disabled={busy}>
                          {busy ? "שומרים…" : `שמרו · ${ticked.size} הגיעו`}
                        </button>
                        <button
                          className="btn"
                          onClick={() => setTicked(new Set(regs.data.map((r) => r.id)))}
                          disabled={busy}
                        >
                          כולם הגיעו
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <div className="card">
              <div className="card-title" style={{ display: "flex", alignItems: "center" }}>
                <span className="grow">פרטים</span>
                {isStaff && !cancelled && (
                  <button className="faint" onClick={() => setEditing(true)} style={{ padding: "2px 6px" }}>
                    עריכה
                  </button>
                )}
              </div>
              <Field label="מקום" value={event.location?.name ?? null} />
              <Field label="כתובת" value={event.location?.address ?? null} />
              <Field
                label="שיוך"
                value={
                  (event.owners ?? [])
                    .map((o) => OWNERS.find((x) => x.slug === o)?.label ?? o)
                    .join(" · ") || null
                }
              />
              <Field label="הרשמה" value={event.registrationOpen === false ? "סגורה" : "פתוחה"} />
              <Field label="מזהה לטפסים" value={event.slug ?? null} mono />
              <Field label="חוזר" value={event.recurrenceText ?? null} />
              {event.description && (
                <div style={{ padding: "2px 14px 12px" }}>
                  <p className="muted">{event.description}</p>
                </div>
              )}
            </div>

            {/* The link the financial work will use. Stated rather than hidden,
                so it is obvious why an event has no budget yet. */}
            <div className="card">
              <div className="card-title">תקציב</div>
              <div className="card-pad">
                <p className="muted">
                  עוד אי אפשר לקשר אירוע לפרויקט, להכנסות ולהוצאות. השדה כבר קיים באירוע,
                  כדי שכשנבנה את זה, לא נצטרך לגעת באירועים קיימים.
                </p>
              </div>
            </div>

            {isAdmin && !cancelled && (
              <div className="card">
                <div className="card-title">ביטול</div>
                {cancelling ? (
                  <div className="card-pad">
                    <p className="muted">
                      נסמן את האירוע כמבוטל ונסגור את ההרשמה. הוא לא נמחק. ההרשמות נשארות, כדי
                      שתוכלו להודיע לנרשמים.
                    </p>
                    {/* One Friday or all of them. Asked, never inferred: guessing
                        between the two is how a community turns up to a locked
                        door. Past occurrences are never touched. */}
                    {event.seriesId && (
                      <div className="chips" style={{ marginTop: 10 }}>
                        <button
                          className={cancelScope === "one" ? "tagchip tagchip-on" : "tagchip"}
                          onClick={() => setCancelScope("one")}
                          disabled={busy}
                        >
                          רק המפגש הזה
                        </button>
                        <button
                          className={cancelScope === "series" ? "tagchip tagchip-on" : "tagchip"}
                          onClick={() => setCancelScope("series")}
                          disabled={busy}
                        >
                          כל המפגשים הבאים
                        </button>
                      </div>
                    )}
                    <input
                      className="form-input"
                      style={{ marginTop: 8 }}
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="סיבה (לא חובה)"
                      disabled={busy}
                    />
                    <div className="btn-row" style={{ marginTop: 10 }}>
                      <button className="btn" onClick={doCancel} disabled={busy}>
                        <span className="danger">{busy ? "מבטלים…" : "בטלו את האירוע"}</span>
                      </button>
                      <button className="btn" onClick={() => setCancelling(false)} disabled={busy}>
                        חזרה
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="list-row" onClick={() => setCancelling(true)}>
                    <span
                      className="rail-ico tint-red" style={{ width: 34, height: 34 }}
                    >
                      <IcAlert size={16} />
                    </span>
                    <span className="grow">
                      <span className="danger">ביטול האירוע</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {editing && <EventSheet event={event} onClose={() => setEditing(false)} />}
    </div>
  );
}

function RegRow({
  reg,
  name,
  last,
  checkIn,
  ticked,
  onToggle,
}: {
  reg: EventRegistration;
  name: string;
  last: boolean;
  checkIn: boolean;
  ticked: boolean;
  onToggle: () => void;
}) {
  const adults = reg.guestCounts?.adults ?? 0;
  const children = reg.guestCounts?.children ?? 0;
  const label = name || "בלי שם";
  const border = last ? "0" : undefined;

  const body = (
    <>
      {checkIn && (
        <span className={ticked ? "selbox on" : "selbox"} aria-hidden>
          {ticked && <IcCheck size={14} />}
        </span>
      )}
      <Avatar name={label} size={36} />
      <span className="grow">
        <span className="name-row">
          {label}
        </span>
        <span className="faint" style={{ display: "block", marginTop: 2 }}>
          {[
            adults + children > 0 ? `${adults} מבוגרים · ${children} ילדים` : null,
            reg.answers?.notes,
          ]
            .filter(Boolean)
            .join(" · ") || "נרשם"}
        </span>
      </span>
      {!checkIn && reg.attended === true && (
        <span className="rec-badge rec-family" style={{ flexShrink: 0, marginTop: 0 }}>
          הגיע
        </span>
      )}
      {!checkIn && reg.primaryPersonId && <IcChevron size={15} />}
    </>
  );

  if (checkIn) {
    return (
      <button
        className="card-body row"
        onClick={onToggle}
        style={{
          borderBottom: border ?? "1px solid var(--line)",
          alignItems: "center",
          width: "100%",
          textAlign: "start",
          background: ticked ? "var(--green-soft)" : undefined,
        }}
      >
        {body}
      </button>
    );
  }

  return reg.primaryPersonId ? (
    <Link
      to={`/people/${reg.primaryPersonId}`}
      className="card-body row"
      style={{ borderBottom: border ?? "1px solid var(--line)", alignItems: "center" }}
    >
      {body}
    </Link>
  ) : (
    <div
      className="card-body row"
      style={{ borderBottom: border ?? "1px solid var(--line)", alignItems: "center" }}
    >
      {body}
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className={mono ? "field-value mono" : "field-value"}>{value || "—"}</span>
    </div>
  );
}

export const EVENT_ICON = IcPeople;
